import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { ClassbookDialogInput, ClassbookLernfeld } from 'src/app/models/classbook-dialog.model';
import { DialogManagerService } from 'src/app/services/dialog-manager.service';
import { DocentClassbookService } from 'src/app/services/docent-classbook.service';
import { NotificationService } from 'src/app/services/notification.service';
import { toIsoDate } from 'src/app/utils/date-helper';

/** Gespeicherte Stunde = Upsert; `isOriginal`: korrigierbar, nicht löschbar. */
interface ClassbookDialogRow {
  hour: number;
  lernfeldContentGroupId: number | null;
  remark: string;
  isOriginal: boolean;
  /** Nur geänderte speichern, alte nicht nachpflegen müssen. */
  dirty: boolean;
}

interface LernfeldGroupOption {
  label: string;
  items: { id: number; name: string }[];
}

// wie im Backend
const MAX_REMARK_LENGTH = 2000;

@Component({
  selector: 'app-classbook-entry-dialog',
  standalone: true,
  templateUrl: './classbook-entry-dialog.component.html',
  styleUrls: ['./classbook-entry-dialog.component.scss'],
  imports: [
    DatePipe,
    FormsModule,
    ButtonModule,
    DropdownModule,
    InputTextareaModule,
    ProgressSpinnerModule,
    TooltipModule,
  ],
})
export class ClassbookEntryDialogComponent implements OnInit {
  entry!: ClassbookDialogInput;

  loading = true;
  submitting = false;

  /** Kein Klassenbuch oder Kurs nicht auffindbar */
  unavailable = false;

  readonly maxRemarkLength = MAX_REMARK_LENGTH;

  lernfeldOptions: LernfeldGroupOption[] = [];
  rows: ClassbookDialogRow[] = [];

  existingCount = 0;

  // Gesetzt → öffentlicher Endpunkt per Hash
  private hash?: string;

  constructor(
    private readonly classbookService: DocentClassbookService,
    private readonly notificationService: NotificationService,
    private readonly config: DynamicDialogConfig,
    private readonly ref: DynamicDialogRef,
    private readonly dialogManager: DialogManagerService,
  ) {}

  ngOnInit(): void {
    this.entry = this.config.data?.entry;
    this.hash = this.config.data?.hash;
    this.loadContext();
  }

  addRow(): void {
    this.rows.push({
      hour: this.nextFreeHour(),
      lernfeldContentGroupId: null,
      remark: '',
      isOriginal: false,
      dirty: true,
    });
  }

  removeRow(row: ClassbookDialogRow): void {
    this.rows = this.rows.filter((r) => r !== row);
  }

  markDirty(row: ClassbookDialogRow): void {
    row.dirty = true;
  }

  save(): void {
    const entries = this.rows.filter((row) => row.dirty);

    if (entries.length === 0) {
      this.notificationService.warn(
        this.rows.length === 0
          ? 'Bitte fügen Sie mindestens einen Eintrag hinzu.'
          : 'Es gibt keine Änderungen zu speichern.',
      );
      return;
    }

    if (entries.some((row) => !row.lernfeldContentGroupId)) {
      this.notificationService.warn(
        'Bitte wählen Sie für alle Einträge einen Unterrichtsinhalt aus oder entfernen Sie den Eintrag.',
      );
      return;
    }

    this.submitting = true;
    this.classbookService
      .saveEntries(
        {
          courseName: this.entry.courseName,
          date: toIsoDate(new Date(this.entry.date)),
          docentName: this.entry.docentName,
          entries: entries.map((row) => ({
            hour: row.hour,
            lernfeldContentGroupId: row.lernfeldContentGroupId!,
            remark: row.remark?.trim() || null,
          })),
        },
        this.hash,
      )
      .subscribe({
        next: (result) => {
          this.notificationService.success(result.message);
          this.close(true);
        },
        error: (err) => {
          this.submitting = false;
          this.notificationService.error(
            'Klassenbuch-Eintrag konnte nicht gespeichert werden',
            err,
          );
        },
      });
  }

  close(saved = false): void {
    this.dialogManager.closeClassbookEntryDialog();
    this.ref.close(saved);
  }

  private loadContext(): void {
    this.classbookService
      .getContext(
        this.entry.courseName,
        toIsoDate(new Date(this.entry.date)),
        this.entry.docentName,
        this.hash,
      )
      .subscribe({
        next: (context) => {
          this.loading = false;

          if (!context.classbookEnabled) {
            this.unavailable = true;
            return;
          }

          const entries = context.entries ?? [];
          this.buildLernfeldOptions(context.lernfelder ?? []);
          this.existingCount = entries.length;

          this.rows = entries
            .slice()
            .sort((a, b) => a.hour - b.hour)
            .map((existing) => ({
              hour: existing.hour,
              lernfeldContentGroupId: existing.lernfeldContentGroupId ?? null,
              remark: existing.remark ?? '',
              isOriginal: true,
              dirty: false,
            }));

          // Leere Zeile nur ohne vorhandene Einträge
          if (this.rows.length === 0) {
            this.addRow();
          }
        },
        error: (err) => {
          this.loading = false;
          this.unavailable = true;
          this.notificationService.error(
            'Klassenbuch konnte nicht geladen werden',
            err,
          );
        },
      });
  }

  private buildLernfeldOptions(lernfelder: ClassbookLernfeld[]): void {
    const toOption = (l: ClassbookLernfeld) => ({ id: l.lernfeldContentGroupId, name: l.label });
    const numbered = lernfelder.filter((l) => l.lernfeldNumber != null);
    const others = lernfelder.filter((l) => l.lernfeldNumber == null);

    const options: LernfeldGroupOption[] = [];
    if (numbered.length > 0) {
      options.push({ label: 'Lernfelder', items: numbered.map(toOption) });
    }
    if (others.length > 0) {
      options.push({ label: 'Sonstige Inhalte', items: others.map(toOption) });
    }
    this.lernfeldOptions = options;
  }

  // Stunde nur zur Unterscheidung, kein Eingabefeld
  private nextFreeHour(): number {
    const used = new Set(this.rows.map((row) => row.hour));
    let hour = 1;
    while (used.has(hour)) {
      hour++;
    }
    return hour;
  }
}
